import React, { useCallback, useLayoutEffect } from 'react';

import {
  LoadingAnimation,
  useResponsive,
  useSafeExpoNavigation,
  useSafeFocusEffect,
  useSafeNavigation,
  useToggleState,
} from '@sg/garnish';
import { IngredientAssetVersion, type Vendor } from '@sg/graphql-schema';
import {
  type CustomizationMachineTypes,
  useCustomizationContext,
  useCustomizationHelpers,
  useInitCustomizationMachine,
} from '@sg/shared-fe/features/customization';

import { NotFoundView } from '../../../../components';
import { useBraze } from '../../../../context/Braze';
import { useSetLastInteractedMenuStore } from '../../../../context/LastInteractedStore';
import { useSnapPixel } from '../../../../integrations';
import { useCustomizationContent } from '../../hooks';
import { useCustomizationExternalActions, useCustomizeCartMutations } from './hooks';
import { CustomizationCustomizeView, CustomizationDetailsView } from './views';

// ─────────────────────────────────────────────────────────────────────────────

export const Customization = (props: Props) => {
  const { restaurantSlug, productSlug } = props;

  // ─── Missing Data ────────────────────────────────────────────────────

  if (!restaurantSlug || !productSlug) {
    return <NotFoundView />;
  }

  // ─────────────────────────────────────────────────────────────────────

  return <CustomizationReady {...props} />;
};

const CustomizationReady = (props: Props) => {
  const {
    restaurantSlug,
    productSlug,
    addressId,
    deliveryFee,
    vendor,
    reorderLineItemId,
    lineItemId,
    isEditProductMode,
    onAddLineItemToCart,
    onEditLineItemInCart,
    onRemoveLineItemFromCart,
  } = props;

  const navigation = useSafeNavigation();
  const expoNavigation = useSafeExpoNavigation();
  const setOptions = navigation?.setOptions ?? expoNavigation?.setOptions;
  const { currentBreakpoint } = useResponsive();
  const { logCustomEvent } = useBraze();
  const { trackViewContent: trackSnapPixelViewContent } = useSnapPixel();

  // ─── Content ─────────────────────────────────────────────────────────

  const customizationContent = useCustomizationContent();

  // ─── State ───────────────────────────────────────────────────────────

  const {
    value: isOutOfStockModalOpen,
    toggleOn: openOutOfStockModal,
    toggleOff: closeOutOfStockModal,
  } = useToggleState();

  // ─── Line Item Interactions ──────────────────────────────────────────

  const { modifyCart, isAddingLineItemToCart, isEditingLineItemInCart } = useCustomizeCartMutations(
    {
      addressId,
      deliveryFee,
      vendor,
      onAddLineItemToCart,
      onEditLineItemInCart,
      onRemoveLineItemFromCart,
    },
  );

  const shouldDisableControls = isAddingLineItemToCart || isEditingLineItemInCart;

  // ─────────────────────────────────────────────────────────────────────

  const externalActions = useCustomizationExternalActions({
    handleCustomizationComplete: modifyCart,
  });

  const handleOutOfStockIngredientsFound =
    useCallback<CustomizationMachineTypes.CustomizationMachineAction>(
      (...params) => {
        externalActions.onOutOfStockIngredientsFound?.(...params);
        openOutOfStockModal();
      },
      [externalActions, openOutOfStockModal],
    );

  const actor = useInitCustomizationMachine(
    {
      productSlug,
      restaurantSlug,
      lineItemId: lineItemId ?? reorderLineItemId,
      ingredientAssetVersion: IngredientAssetVersion.V2,
      isDelivery: Boolean(addressId),
    },
    {
      actions: {
        ...externalActions,
        onOutOfStockIngredientsFound: handleOutOfStockIngredientsFound,
      },
      guards: {
        checkIfShouldStartCustomization: () => true,
      },
    },
  );

  // ─── Context ─────────────────────────────────────────────────────────

  const { isFetching, hasFailedToFetch, isCustomizationActive, product, restaurant } =
    useCustomizationContext({ actor });

  // ─── Helpers ─────────────────────────────────────────────────────────

  const { completeCustomization } = useCustomizationHelpers({ actor });

  const trackCustomizationViewBrazeEvent = useCallback(() => {
    logCustomEvent('pdp.view');
  }, [logCustomEvent]);

  const trackCustomizationViewSnapPixelEvent = useCallback(() => {
    if (!product) return;

    trackSnapPixelViewContent({
      productId: product.id,
      productName: product.name,
    });
  }, [trackSnapPixelViewContent, product]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useLayoutEffect(() => {
    setOptions?.({ headerShown: !currentBreakpoint.isXS });
  }, [currentBreakpoint.isXS, setOptions]);

  useSetLastInteractedMenuStore({
    restaurantId: restaurant?.id,
    restaurantName: restaurant?.name,
    restaurantSlug: restaurant?.slug,
    addressId,
    deliveryFee,
    vendor: vendor?.name,
    vendorRestaurantId: vendor?.restaurantId,
  });

  useSafeFocusEffect(trackCustomizationViewBrazeEvent);
  useSafeFocusEffect(trackCustomizationViewSnapPixelEvent);

  // ─────────────────────────────────────────────────────────────────────

  if (isFetching) {
    return <LoadingAnimation />;
  }

  if (hasFailedToFetch || !product || !restaurant) {
    return <NotFoundView />;
  }

  // ─── Customization ───────────────────────────────────────────────────

  if (isCustomizationActive) {
    return (
      <CustomizationCustomizeView
        actor={actor}
        customizationContent={customizationContent}
        addressId={addressId}
        isOutOfStockModalOpen={isOutOfStockModalOpen}
        shouldDisableControls={shouldDisableControls}
        closeOutOfStockModal={closeOutOfStockModal}
      />
    );
  }

  // ─── Dressing Details ────────────────────────────────────────────────

  return (
    <CustomizationDetailsView
      actor={actor}
      customizationContent={customizationContent}
      addressId={addressId}
      isOutpost={restaurant.isOutpost}
      isEditProductMode={isEditProductMode}
      shouldDisableControls={shouldDisableControls}
      isOutOfStockModalOpen={isOutOfStockModalOpen}
      closeOutOfStockModal={closeOutOfStockModal}
      completeCustomization={completeCustomization}
    />
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  restaurantSlug: string;
  productSlug: string;
  reorderLineItemId?: string;
  lineItemId?: string;
  addressId?: string;
  deliveryFee?: number;
  vendor?: Vendor;
  isEditProductMode: boolean;
  onAddLineItemToCart?: () => void;
  onEditLineItemInCart?: () => void;
  onRemoveLineItemFromCart?: () => void;
};
